// core/services/prefsService.ts — async personalisation service.
//
// Provides the public async API for reading and writing UserPreferences.
// All mutations update prefsStore (reactive UI) AND write to localStorage
// under a per-account key so prefs survive page reloads.
//
// Per-account key strategy:
//   signed-in  → 'truxsocial:prefs:@handle'
//   signed-out → 'truxsocial:prefs:guest'
//
// This service is called:
//   - At app boot (main.tsx) to hydrate the store for the current account.
//   - By authService after sign-in / sign-out to swap account prefs.
//   - By usePreferences hook to expose mutations to components.
//
// Components must NOT call this service directly — use hooks/usePreferences.

import type { UserPreferences, BackdropKey, RadiusKey } from '@/core/models/preferences';
import { normalizeAccountMenu } from '@/core/models/accountMenu';
import {
  RADIUS_PX_RANGE,
  CARD_ELEVATION_DEFAULT,
  clampUnit,
  clampRadiusHard,
  clampRadiusBounds,
} from '@/core/models/preferences';
import { LEGACY_RADIUS_PX } from '@/theme/presets/radius';
import { quantiseGrain } from '@/theme/presets/cardMaterial';
import {
  PANEL_SOLIDITY_RANGE,
  clampSolidity,
  BG_INTENSITY_RANGE,
  clampBgIntensity,
  GLASS_BLUR_RANGE,
  clampGlassBlur,
  GLASS_TINT_RANGE,
  clampGlassTint,
  GLASS_SATURATION_RANGE,
  clampGlassSaturation,
} from '@/core/models/preferences';
import { usePrefsStore, DEFAULT_PREFS } from '@/core/stores/prefsStore';
import { useSessionStore } from '@/core/stores/sessionStore';

const PREFS_KEY_PREFIX = 'truxsocial:prefs';

function getStorageKey(): string {
  const accounts = useSessionStore.getState().accounts;
  const active = accounts.find((a) => a.useThisAsActive);
  return active ? `${PREFS_KEY_PREFIX}:${active.handle}` : `${PREFS_KEY_PREFIX}:guest`;
}

/* Shared coercion helpers for the stored-record readers below. */
const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const hexOr = (raw: unknown, fb: string): string =>
  typeof raw === 'string' && HEX_RE.test(raw) ? raw : fb;
const numOr = (raw: unknown, fb: number, clamp: (n: number) => number): number =>
  typeof raw === 'number' && Number.isFinite(raw) ? clamp(raw) : fb;

function coercePanelSolidity(raw: unknown): number {
  // Old records may carry the string keys 'standard' or 'solid' from the
  // two-state enum era.  Non-numbers (including those old strings) fall back
  // to the default; finite numbers are clamped into the allowed range.
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return PANEL_SOLIDITY_RANGE.default;
  return clampSolidity(raw);
}

function coerceBackdrop(raw: unknown): BackdropKey {
  if (raw === 'auto' || raw === 'high' || raw === 'low' || raw === 'off') return raw;
  // Migrate the old enum: animated → auto, static → low.
  if (raw === 'animated') return 'auto';
  if (raw === 'static') return 'low';
  return DEFAULT_PREFS.backdrop;
}

function readFromStorage(key: string): UserPreferences {
  try {
    return normalizePreferences(JSON.parse(localStorage.getItem(key) ?? 'null'));
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function normalizePreferences(value: unknown): UserPreferences {
  try {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return { ...DEFAULT_PREFS };
    const parsed = value as Partial<Record<string, unknown>>;
    // Merge with defaults to handle partial/stale records
    const merged: UserPreferences = { ...DEFAULT_PREFS, ...(parsed as Partial<UserPreferences>) };
    const enums = {
      themeMode: ['dark', 'light'],
      accent: ['default', 'teal', 'sunset', 'aurora', 'ember', 'mint', 'magma', 'custom'],
      finish: ['flat', 'metallic', 'glow'],
      textSize: ['sm', 'md', 'lg', 'xl'],
    } as const;
    for (const key of Object.keys(enums) as (keyof typeof enums)[]) {
      if (!(enums[key] as readonly unknown[]).includes(parsed[key]))
        Object.assign(merged, { [key]: DEFAULT_PREFS[key] });
    }
    merged.accentCustom = hexOr(parsed['accentCustom'], DEFAULT_PREFS.accentCustom);
    merged.reduceMotion = parsed['reduceMotion'] === true;
    merged.bannerAnimations = parsed['bannerAnimations'] === true;
    merged.accountMenuShortcuts = normalizeAccountMenu(parsed['accountMenuShortcuts']);
    // Coerce panelSolidity: old string values ('standard'/'solid') or garbage → default/clamped number
    merged.panelSolidity = coercePanelSolidity(parsed['panelSolidity']);
    // Migrate/coerce the backdrop tier + cards-overlay from older records.
    merged.backdrop = coerceBackdrop(parsed['backdrop']);
    merged.cardsOverlay =
      typeof parsed['cardsOverlay'] === 'boolean'
        ? parsed['cardsOverlay']
        : DEFAULT_PREFS.cardsOverlay;
    // Accessibility booleans — coerce garbage to their (off) defaults.
    merged.underlineLinks =
      typeof parsed['underlineLinks'] === 'boolean'
        ? parsed['underlineLinks']
        : DEFAULT_PREFS.underlineLinks;
    merged.reduceTransparency =
      typeof parsed['reduceTransparency'] === 'boolean'
        ? parsed['reduceTransparency']
        : DEFAULT_PREFS.reduceTransparency;
    // Language & Region enums — coerce anything unknown to the defaults.
    merged.timestampStyle =
      parsed['timestampStyle'] === 'absolute' ? 'absolute' : DEFAULT_PREFS.timestampStyle;
    merged.clockFormat = parsed['clockFormat'] === '24h' ? '24h' : DEFAULT_PREFS.clockFormat;
    merged.rememberFeedPosition = parsed['rememberFeedPosition'] === true;
    // Content Preferences enums.
    merged.defaultFeedTab = (['following', 'for-you', 'trending', 'discover'] as const).includes(
      parsed['defaultFeedTab'] as never,
    )
      ? (parsed['defaultFeedTab'] as UserPreferences['defaultFeedTab'])
      : DEFAULT_PREFS.defaultFeedTab;
    merged.feedDensity =
      parsed['feedDensity'] === 'compact' ? 'compact' : DEFAULT_PREFS.feedDensity;
    merged.feedOrder = ['default', 'newest', 'most-liked', 'oldest'].includes(
      parsed['feedOrder'] as string,
    )
      ? (parsed['feedOrder'] as UserPreferences['feedOrder'])
      : DEFAULT_PREFS.feedOrder;
    merged.bgIntensity =
      typeof parsed['bgIntensity'] === 'number' && Number.isFinite(parsed['bgIntensity'])
        ? clampBgIntensity(parsed['bgIntensity'])
        : BG_INTENSITY_RANGE.default;
    // Glass personalisation — coerce each numeric knob (clamp finite numbers,
    // fall back to default for stale/garbage values).
    merged.glassBlur =
      typeof parsed['glassBlur'] === 'number' && Number.isFinite(parsed['glassBlur'])
        ? clampGlassBlur(parsed['glassBlur'])
        : GLASS_BLUR_RANGE.default;
    merged.glassTint =
      typeof parsed['glassTint'] === 'number' && Number.isFinite(parsed['glassTint'])
        ? clampGlassTint(parsed['glassTint'])
        : GLASS_TINT_RANGE.default;
    merged.glassSaturation =
      typeof parsed['glassSaturation'] === 'number' && Number.isFinite(parsed['glassSaturation'])
        ? clampGlassSaturation(parsed['glassSaturation'])
        : GLASS_SATURATION_RANGE.default;

    // ── Card colour + material ──────────────────────────────────────────────
    merged.cardColor = hexOr(parsed['cardColor'], DEFAULT_PREFS.cardColor);
    merged.cardColorStrength = numOr(parsed['cardColorStrength'], 0, clampUnit);
    merged.cardSheen = numOr(parsed['cardSheen'], 0, clampUnit);
    merged.cardGrain = quantiseGrain(numOr(parsed['cardGrain'], 0, clampUnit));
    merged.cardEdge = numOr(parsed['cardEdge'], 0, clampUnit);
    merged.cardElevation = numOr(parsed['cardElevation'], CARD_ELEVATION_DEFAULT, clampUnit);
    merged.overlayGradient = ['none', 'accent', 'aurora', 'sunset'].includes(
      parsed['overlayGradient'] as string,
    )
      ? (parsed['overlayGradient'] as UserPreferences['overlayGradient'])
      : DEFAULT_PREFS.overlayGradient;
    merged.overlayGradientStrength = numOr(
      parsed['overlayGradientStrength'],
      DEFAULT_PREFS.overlayGradientStrength,
      clampUnit,
    );
    // Older accounts had one intensity for every surface. Keep that value for
    // overlays and seed the two new controls proportionally, including zero.
    merged.postGradientStrength = numOr(
      parsed['postGradientStrength'],
      merged.overlayGradientStrength * (30 / 65),
      clampUnit,
    );
    merged.panelGradientStrength = numOr(
      parsed['panelGradientStrength'],
      merged.overlayGradientStrength * (45 / 65),
      clampUnit,
    );

    // ── Radius: repair the bounds first, then numeric wins → legacy enum → default ──
    const [lo, hi] = clampRadiusBounds(
      numOr(parsed['radiusMinPx'], RADIUS_PX_RANGE.defaultMin, clampRadiusHard),
      numOr(parsed['radiusMaxPx'], RADIUS_PX_RANGE.defaultMax, clampRadiusHard),
    );
    merged.radiusMinPx = lo;
    merged.radiusMaxPx = hi;
    const legacyPx = LEGACY_RADIUS_PX[parsed['radius'] as RadiusKey] ?? RADIUS_PX_RANGE.default;
    merged.radiusPx = Math.min(
      hi,
      Math.max(lo, numOr(parsed['radiusPx'], legacyPx, clampRadiusHard)),
    );
    // `merged` starts as { ...DEFAULT_PREFS, ...parsed } — without this the retired enum
    // key survives into storage forever.
    delete (merged as unknown as Record<string, unknown>)['radius'];

    return merged;
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

function writeToStorage(key: string, prefs: UserPreferences): void {
  try {
    if (!prefs.rememberFeedPosition) {
      const owner =
        useSessionStore.getState().accounts.find((a) => a.useThisAsActive)?.userId ?? 'guest';
      try {
        sessionStorage.removeItem(`truxsocial:feed-visit:${owner}`);
      } catch {
        /* session storage unavailable */
      }
    }
    localStorage.setItem(key, JSON.stringify(prefs));
  } catch {
    // localStorage quota or SSR — ignore
  }
}

export const prefsService = {
  /** Apply one coherent change without intermediate store or storage writes. */
  async updatePreferences(patch: Partial<UserPreferences>): Promise<void> {
    const updated = { ...usePrefsStore.getState().prefs, ...patch };
    usePrefsStore.getState().setPrefs(updated);
    writeToStorage(getStorageKey(), updated);
  },
  /**
   * Read the active account's preferences from localStorage and hydrate the
   * store.  Call at boot and after every sign-in / sign-out transition.
   */
  async getPreferences(): Promise<UserPreferences> {
    const key = getStorageKey();
    const prefs = readFromStorage(key);
    usePrefsStore.getState().setPrefs(prefs);
    return prefs;
  },

  /**
   * Set a single preference key, update the store, and persist.
   */
  async setPreference<K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K],
  ): Promise<void> {
    usePrefsStore.getState().setPref(key, value);
    const updated = usePrefsStore.getState().prefs;
    writeToStorage(getStorageKey(), updated);
  },

  /**
   * Reset all preferences to defaults for the active account.
   */
  async resetPreferences(): Promise<void> {
    usePrefsStore.getState().reset();
    writeToStorage(getStorageKey(), { ...DEFAULT_PREFS });
  },
};
